import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import TrustBadge from "./TrustBadge";
import { toast } from "sonner";
import { Sparkles, MessagesSquare, Loader2, Quote, ListTree, ChevronRight, ChevronDown } from "lucide-react";

/**
 * Ask Marianne — per-agreement Q&A and clause analysis.
 * Renders inside the AgreementsPage as a collapsible panel per row.
 */
export default function AskMariannePanel({ agreementId, agreementTitle, alwaysOpen = false }) {
  const [open, setOpen] = useState(alwaysOpen);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [clauses, setClauses] = useState(null);
  const [analysing, setAnalysing] = useState(false);

  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/agreements/ask", { agreement_id: agreementId, question: q.trim() });
      setAnswer(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Question failed");
    } finally {
      setAsking(false);
    }
  };

  const analyseClauses = async () => {
    setAnalysing(true);
    try {
      const { data } = await api.post("/agreements/analyze-clauses", { agreement_id: agreementId });
      setClauses(data);
      toast.success(`Marianne identified ${data.clauses.length} clauses.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalysing(false);
    }
  };

  return (
    <div className="border-t border-[#E8E3F0]" data-testid={`ask-panel-${agreementId}`}>
      {!alwaysOpen && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-3 text-sm hover:bg-[#FAFAF8]"
          data-testid={`ask-panel-toggle-${agreementId}`}
        >
          <span className="flex items-center gap-2 label-eyebrow">
            <MessagesSquare size={13} className="text-[#7B56C9]" /> Ask Marianne about this agreement
          </span>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      )}
      {open && (
        <div className="px-5 pb-5 space-y-4 bg-[#FAFAF8]/50">
          {/* Q&A */}
          <div>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. When does it renew? What's the notice period? Who owns the IP?"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                className="rounded-none border-[#E8E3F0]"
                data-testid={`ask-input-${agreementId}`}
              />
              <Button
                onClick={ask}
                disabled={asking || !q.trim()}
                className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
                data-testid={`ask-btn-${agreementId}`}
              >
                {asking ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              </Button>
            </div>
            {answer && (
              <div className="mt-3 space-y-2" data-testid={`ask-answer-${agreementId}`}>
                <div className="text-sm bg-white border border-[#E8E3F0] p-3">{answer.answer}</div>
                {answer.quoted_clause && (
                  <div className="border-l-2 border-[#7B56C9] bg-[#7B56C9]/5 px-3 py-2 text-xs italic flex gap-2">
                    <Quote size={12} className="text-[#7B56C9] shrink-0 mt-0.5" />
                    <span>"{answer.quoted_clause}"</span>
                  </div>
                )}
                {answer.trust && <TrustBadge trust={answer.trust} compact />}
              </div>
            )}
          </div>

          {/* Clause analysis */}
          <div>
            <Button
              onClick={analyseClauses}
              disabled={analysing}
              variant="outline"
              size="sm"
              className="rounded-none border-[#E8E3F0]"
              data-testid={`analyze-clauses-btn-${agreementId}`}
            >
              {analysing ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> Analysing…</> : <><ListTree size={12} className="mr-1.5" /> Identify clauses</>}
            </Button>
            {clauses && (
              <div className="mt-3 space-y-2" data-testid={`clauses-list-${agreementId}`}>
                <ul className="text-xs space-y-1 divide-y divide-[#E8E3F0] border border-[#E8E3F0] bg-white">
                  {clauses.clauses.map((c, i) => (
                    <li key={i} className="px-3 py-2">
                      <div className="flex items-baseline gap-2">
                        <span className="label-eyebrow !text-[10px] shrink-0">{c.number}</span>
                        <span className="font-medium">{c.heading}</span>
                      </div>
                      {c.summary && <div className="text-[#4A5568] mt-0.5">{c.summary}</div>}
                    </li>
                  ))}
                </ul>
                {clauses.trust && <TrustBadge trust={clauses.trust} compact />}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
